import { RefreshCw, Share, WifiOff } from 'lucide-react'
import type { ReactNode } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

import { Button } from '@damis/ui/components/button'
import { cn } from '@damis/ui/lib/utils'
import { BrandMark } from '@/components/layout/brand'

import { dismissInstall, install, type InstallOffer, useInstallOffer } from './install'
import { useOnline } from './online'
import { reloadIntoUpdate } from './update'

/** How often an open app asks whether a new build is out: a tab can stay open for days. */
const UPDATE_CHECK_MS = 60 * 60 * 1000

/**
 * The installed app's notices, over every screen, signed in or not: no network, a new build to
 * reload into, and the offer to install. Each is a card lifted by `shadow-menu` with 14px corners,
 * as the save bar is (docs/design/design-system/components/SaveBar), at the foot of the screen:
 * clear of the tab bar on a phone, in the corner on a computer.
 */
export function AppNotices() {
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-4 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] z-30 flex flex-col gap-2 md:inset-x-auto md:right-7 md:bottom-7 md:w-96"
    >
      <OfflineNotice />
      <UpdateNotice />
      <InstallNotice />
    </div>
  )
}

function Notice({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-[14px] border bg-popover px-4 py-3 text-popover-foreground shadow-menu',
        className,
      )}
    >
      {children}
    </div>
  )
}

function OfflineNotice() {
  if (useOnline()) return null
  return (
    <Notice className="items-center py-2.5">
      <WifiOff className="size-4 shrink-0 text-warn" aria-hidden />
      <p role="status" className="text-sm">
        You're offline. Showing what was last loaded; changes save once you reconnect.
      </p>
    </Notice>
  )
}

function UpdateNotice() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration) setInterval(() => void registration.update(), UPDATE_CHECK_MS)
    },
  })
  if (!needRefresh) return null
  return (
    <Notice className="items-center">
      <RefreshCw className="size-4 shrink-0 text-primary" aria-hidden />
      <p role="status" className="min-w-0 flex-1 text-sm">
        A new version of DAMIS Eval is ready.
      </p>
      <Button size="sm" onClick={() => void reloadIntoUpdate(updateServiceWorker)}>
        Reload
      </Button>
    </Notice>
  )
}

const howTo: Record<Exclude<InstallOffer, null>, ReactNode> = {
  prompt:
    'Install it on this device: it opens in its own window, loads faster, and keeps recent evaluations to hand with no signal.',
  ios: (
    <>
      Tap <Share className="inline size-3.5 -translate-y-px" aria-label="Share" /> Share, then{' '}
      <span className="font-medium text-foreground">Add to Home Screen</span>. It opens full screen,
      loads faster, and works with no signal.
    </>
  ),
  menu: (
    <>
      Open your browser's menu and choose{' '}
      <span className="font-medium text-foreground">Install app</span> or{' '}
      <span className="font-medium text-foreground">Add to Home screen</span>. It opens full screen,
      loads faster, and works with no signal.
    </>
  ),
}

function InstallNotice() {
  const offer = useInstallOffer()
  if (offer === null) return null
  return (
    <Notice>
      <BrandMark className="size-10" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div>
          <p className="text-sm font-semibold">Get the DAMIS Eval app</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{howTo[offer]}</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => dismissInstall()}>
            Not now
          </Button>
          {offer === 'prompt' && (
            <Button size="sm" onClick={() => void install()}>
              Install
            </Button>
          )}
        </div>
      </div>
    </Notice>
  )
}
